import api from './auth';

/**
 * Memory-efficient cache implementation with size limits and automatic cleanup
 */
class MemoryEfficientCache {
  constructor(maxSize = 500, cacheExpiry = 5 * 60 * 1000) {
    this.maxSize = maxSize;
    this.cacheExpiry = cacheExpiry;
    this.cache = new Map();
  }

  get(key) {
    if (this.cache.has(key)) {
      const entry = this.cache.get(key);
      const now = Date.now();

      if (now - entry.timestamp < this.cacheExpiry) {
        // Move to end to mark as recently used (LRU)
        this.cache.delete(key);
        this.cache.set(key, entry);
        return entry.count;
      } else {
        this.cache.delete(key); // Clean up expired entry
      }
    }
    return undefined;
  }

  set(key, count) {
    // Clean up expired entries first
    this.cleanupExpired();

    // If at max size, remove oldest entries (LRU)
    if (this.cache.size >= this.maxSize) {
      const entriesToRemove = Math.max(1, Math.floor(this.maxSize * 0.2)); // Remove 20%
      let removed = 0;
      for (const oldestKey of this.cache.keys()) {
        this.cache.delete(oldestKey);
        removed++;
        if (removed >= entriesToRemove) break;
      }
      console.log(`Cache size limit reached. Removed ${removed} oldest entries`);
    }

    this.cache.set(key, {
      count: count,
      timestamp: Date.now()
    });
  }

  has(key) {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    if (now - entry.timestamp >= this.cacheExpiry) {
      this.cache.delete(key);
      return false;
    }
    return true;
  }

  cleanupExpired() {
    const now = Date.now();
    const expiredKeys = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= this.cacheExpiry) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => this.cache.delete(key));

    if (expiredKeys.length > 0) {
      console.log(`Cleaned up ${expiredKeys.length} expired cache entries`);
    }
  }

  clear() {
    this.cache.clear();
    console.log('Cache cleared manually');
  }

  delete(key) {
    const wasDeleted = this.cache.delete(key);
    if (wasDeleted) {
      console.log(`Deleted cache entry for key: ${key}`);
    }
    return wasDeleted;
  }

  size() {
    this.cleanupExpired();
    return this.cache.size;
  }

  getStats() {
    this.cleanupExpired();
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      utilizationPercent: (this.cache.size / this.maxSize) * 100,
      cacheExpiryMs: this.cacheExpiry
    };
  }
}

/**
 * Segment Service
 * Handles all segment-related operations through secure backend endpoints
 * Optimized for performance with batch operations and caching
 */
class SegmentService {
  constructor() {
    // Cache for profile counts to reduce API calls
    this.profileCountCache = new MemoryEfficientCache();

    // Performance optimization toggle
    this.useOptimizedEndpoint = true; // Enable optimized endpoint by default
  }

  /**
   * Toggle between optimized and legacy segment fetching
   */
  toggleOptimization(enabled = null) {
    if (enabled !== null) {
      this.useOptimizedEndpoint = enabled;
    } else {
      this.useOptimizedEndpoint = !this.useOptimizedEndpoint;
    }

    console.log(`🔧 Segment optimization ${this.useOptimizedEndpoint ? 'ENABLED' : 'DISABLED'}`);
    return this.useOptimizedEndpoint;
  }

  /**
   * Get paginated list of segments with filtering and sorting
   * Now automatically uses optimized endpoint when available
   */
  async getSegmentsPaginated({
    page = 1,
    pageSize = 10,
    search = null,
    sortField = 'created',
    sortDirection = 'desc',
    favoritesOnly = false,
    archivedOnly = false,
    signal = null
  } = {}) {

    // Use optimized endpoint if enabled
    if (this.useOptimizedEndpoint) {
      try {
        console.log('🚀 Using optimized segments endpoint');
        return await this.getSegmentsPaginatedOptimized({
          page,
          pageSize,
          search,
          sortField,
          sortDirection,
          favoritesOnly,
          archivedOnly,
          includeCounts: true,
          signal
        });
      } catch (error) {
        console.warn('⚠️  Optimized endpoint failed, falling back to legacy:', error.message);
        // Fall through to legacy method
      }
    }

    console.log('🐌 Using legacy segments endpoint');
    return await this.getSegmentsPaginatedLegacy({
      page,
      pageSize,
      search,
      sortField,
      sortDirection,
      favoritesOnly,
      archivedOnly,
      signal
    });
  }

  /**
   * Legacy paginated segments method (renamed from original)
   * Optimized for performance - no longer fetches profile counts individually
   */
  async getSegmentsPaginatedLegacy({
    page = 1,
    pageSize = 10,
    search = null,
    sortField = 'created',
    sortDirection = 'desc',
    favoritesOnly = false,
    archivedOnly = false,
    signal = null
  } = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
        include_counts: 'false' // Don't fetch counts in main request for performance
      });

      if (search) {
        params.append('search', search);
      }

      if (favoritesOnly) {
        params.append('favorites_only', 'true');
      }

      if (archivedOnly) {
        params.append('archived_only', 'true');
      }

      console.log(`Fetching segments: page=${page}, size=${pageSize}, search=${search || 'none'}, favoritesOnly=${favoritesOnly}, archivedOnly=${archivedOnly}`);
      console.log(`Request URL: /segments/list?${params.toString()}`);

      const response = await api.get(`/segments/list?${params.toString()}`, { signal });

      if (!response.data) {
        throw new Error('No data received from segments endpoint');
      }

      console.log(`Retrieved ${response.data.segments?.length || 0} segments from backend`);

      const result = {
        success: true,
        segments: response.data.segments || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        pageSize: response.data.page_size || pageSize,
        totalPages: response.data.total_pages || 1
      };

      // If the backend included profile counts (when sorting by profiles), cache them
      if (response.data.profile_counts) {
        const now = Date.now();
        console.log(`Caching ${Object.keys(response.data.profile_counts).length} profile counts from backend`);

        for (const [segmentId, count] of Object.entries(response.data.profile_counts)) {
          const cacheKey = `profile_count_${segmentId}`;
          this.profileCountCache.set(cacheKey, count);
        }

        result.profileCounts = response.data.profile_counts;
      }

      return result;
    } catch (error) {
      console.error('Error fetching paginated segments:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch segments');
    }
  }

  /**
   * Get paginated list of segments using the new optimized endpoint
   * This uses the dual-endpoint approach for much better performance
   */
  async getSegmentsPaginatedOptimized({
    page = 1,
    pageSize = 10,
    search = null,
    sortField = 'created',
    sortDirection = 'desc',
    favoritesOnly = false,
    archivedOnly = false,
    includeCounts = true,
    signal = null
  } = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
        include_counts: includeCounts.toString()
      });

      if (search) {
        params.append('search', search);
      }

      if (favoritesOnly) {
        params.append('favorites_only', 'true');
      }

      if (archivedOnly) {
        params.append('archived_only', 'true');
      }

      console.log(`🚀 Fetching segments with optimized endpoint: page=${page}, size=${pageSize}, search=${search || 'none'}`);
      console.log(`Request URL: /segments/list-optimized?${params.toString()}`);

      const response = await api.get(`/segments/list-optimized?${params.toString()}`, { signal });

      if (!response.data) {
        throw new Error('No data received from optimized segments endpoint');
      }

      console.log(`✅ Retrieved ${response.data.segments?.length || 0} segments from optimized endpoint`);

      const result = {
        success: true,
        segments: response.data.segments || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        pageSize: response.data.page_size || pageSize,
        totalPages: response.data.total_pages || 1
      };

      // If the backend included profile counts, cache them
      if (response.data.profile_counts) {
        console.log(`🗄️  Caching ${Object.keys(response.data.profile_counts).length} profile counts from optimized endpoint`);

        for (const [segmentId, count] of Object.entries(response.data.profile_counts)) {
          const cacheKey = `profile_count_${segmentId}`;
          this.profileCountCache.set(cacheKey, count);
        }

        result.profileCounts = response.data.profile_counts;
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching optimized segments:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch segments with optimized endpoint');
    }
  }

  /**
   * Get profile counts for multiple segments in a single batch request
   * This significantly reduces API calls and improves performance
   */
  async getBatchProfileCounts(segmentIds, options = {}) {
    try {
      if (!segmentIds || segmentIds.length === 0) {
        return {};
      }

      console.log(`Fetching batch profile counts for ${segmentIds.length} segments`);

      // Check cache first
      const now = Date.now();
      const cachedCounts = {};
      const segmentsToFetch = [];

      for (const segmentId of segmentIds) {
        const cacheKey = `profile_count_${segmentId}`;
        const cachedEntry = this.profileCountCache.get(cacheKey);

        if (cachedEntry !== undefined) { // Check for undefined explicitly
          cachedCounts[segmentId] = cachedEntry;
        } else {
          segmentsToFetch.push(segmentId);
        }
      }

      console.log(`Found ${Object.keys(cachedCounts).length} cached counts, fetching ${segmentsToFetch.length} from API`);

      // Fetch remaining counts from backend
      let fetchedCounts = {};
      if (segmentsToFetch.length > 0) {
        const response = await api.post('/segments/profile-counts/batch', {
          segment_ids: segmentsToFetch
        }, {
          signal: options.signal
        });

        fetchedCounts = response.data.counts || {};

        // Cache the results
        for (const [segmentId, count] of Object.entries(fetchedCounts)) {
          const cacheKey = `profile_count_${segmentId}`;
          this.profileCountCache.set(cacheKey, count);
        }

        // Log any errors from the batch request
        if (response.data.errors && Object.keys(response.data.errors).length > 0) {
          console.warn('Some profile counts failed to fetch:', response.data.errors);
        }
      }

      // Combine cached and fetched results
      const allCounts = { ...cachedCounts, ...fetchedCounts };
      console.log(`Batch profile counts completed: ${Object.keys(allCounts).length} total counts`);

      return allCounts;
    } catch (error) {
      console.error('Error fetching batch profile counts:', error);
      // Return empty object on error to avoid breaking the UI
      return {};
    }
  }

  /**
   * Get single segment profile count (with caching)
   * Falls back to individual API call if batch is not available
   */
  async getSegmentProfileCount(segmentId) {
    try {
      // Check cache first
      const now = Date.now();
      const cacheKey = `profile_count_${segmentId}`;
      const cachedEntry = this.profileCountCache.get(cacheKey);

      if (cachedEntry !== undefined) { // Check for undefined explicitly
        return cachedEntry;
      }

      console.log(`Fetching individual profile count for segment: ${segmentId}`);

      const response = await api.get(`/segments/${segmentId}/count`);
      const count = response.data.count || 0;

      // Cache the result
      this.profileCountCache.set(cacheKey, count);

      return count;
    } catch (error) {
      console.error(`Error fetching profile count for segment ${segmentId}:`, error);
      return 0; // Return 0 on error to avoid UI issues
    }
  }

  /**
   * Get segment by ID
   */
  async getSegmentById(segmentId) {
    try {
      console.log(`Fetching segment details for: ${segmentId}`);

      const response = await api.get(`/segments/${segmentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching segment ${segmentId}:`, error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch segment details');
    }
  }

  /**
   * Manually add a contact (Unomi profile itemId) to a segment.
   * Returns { success, message, profileIds }.
   */
  async addSegmentMember(segmentId, profileId) {
    try {
      const response = await api.post(
        `/segments/${encodeURIComponent(segmentId)}/members`,
        { profile_id: profileId }
      );
      // Membership changes the segment population; invalidate cached count.
      this.profileCountCache.delete(`profile_count_${segmentId}`);
      return {
        success: response.data?.success !== false,
        message: response.data?.message || 'Contact ajouté au segment.',
        profileIds: response.data?.profile_ids || [],
        added: response.data?.added ?? null,
      };
    } catch (error) {
      console.error(`Error adding member to segment ${segmentId}:`, error);
      throw new Error(
        error.response?.data?.detail || "Échec de l'ajout du contact au segment"
      );
    }
  }

  /**
   * Remove a manually added contact from a segment.
   * Returns { success, message, profileIds }.
   */
  async removeSegmentMember(segmentId, profileId) {
    try {
      const response = await api.delete(
        `/segments/${encodeURIComponent(segmentId)}/members/${encodeURIComponent(profileId)}`
      );
      this.profileCountCache.delete(`profile_count_${segmentId}`);
      return {
        success: response.data?.success !== false,
        message: response.data?.message || 'Contact retiré du segment.',
        profileIds: response.data?.profile_ids || [],
        removed: response.data?.removed ?? null,
      };
    } catch (error) {
      console.error(`Error removing member from segment ${segmentId}:`, error);
      throw new Error(
        error.response?.data?.detail || 'Échec du retrait du contact du segment'
      );
    }
  }

  /**
   * Get the manually pinned profile ids for a segment.
   */
  async getSegmentManualMembers(segmentId) {
    try {
      const response = await api.get(
        `/segments/${encodeURIComponent(segmentId)}/members`
      );
      return response.data?.profile_ids || [];
    } catch (error) {
      console.error(`Error fetching manual members for segment ${segmentId}:`, error);
      return [];
    }
  }

  /**
   * Delete segment
   */
  async deleteSegment(segmentId) {
    try {
      console.log(`Deleting segment: ${segmentId}`);

      const response = await api.delete(`/segments/${segmentId}`);

      console.log('Delete segment response:', response);
      console.log('Delete segment response data:', response.data);
      console.log('Delete segment response status:', response.status);

      // Clear from cache
      const cacheKey = `profile_count_${segmentId}`;
      this.profileCountCache.delete(cacheKey);

      // Check for successful HTTP status codes (200-299)
      if (response.status >= 200 && response.status < 300) {
        // For successful HTTP responses, check the response data
        const isSuccess = response.data?.success !== false; // Default to true if success is undefined
        const message = response.data?.message || 'Segment deleted successfully';

        console.log(`Delete segment result: success=${isSuccess}, message=${message}`);

        return {
          success: isSuccess,
          message: message
        };
      } else {
        // Non-successful HTTP status
        console.error(`Delete segment failed with status: ${response.status}`);
        return {
          success: false,
          message: response.data?.message || response.data?.detail || 'Failed to delete segment'
        };
      }
    } catch (error) {
      console.error(`Error deleting segment ${segmentId}:`, error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);

      // If we have a response, it means the request went through but might have returned an error status
      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to delete segment';

        // Some 4xx errors might still be considered successful deletion in some cases
        if (status === 404) {
          console.log('Segment not found (404) - treating as successful deletion since segment is already gone');
          return {
            success: true,
            message: 'Segment deleted successfully (was already removed)'
          };
        }

        return {
          success: false,
          message: errorMessage
        };
      }

      // Network error or other issues
      return {
        success: false,
        message: error.message || 'Failed to delete segment - network error'
      };
    }
  }

  /**
   * Clone segment
   */
  async cloneSegment(sourceSegmentId, newName, newDescription = null) {
    try {
      console.log(`Cloning segment: ${sourceSegmentId} to ${newName}`);

      const response = await api.post('/segments/clone', {
        source_segment_id: sourceSegmentId,
        new_name: newName,
        new_description: newDescription
      });

      return {
        success: response.data.success !== false,
        segment: response.data.segment,
        message: response.data.message || 'Segment cloned successfully'
      };
    } catch (error) {
      console.error(`Error cloning segment ${sourceSegmentId}:`, error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to clone segment'
      };
    }
  }

  /**
   * Create segment manually (no LLM)
   */
  async createSegmentManual(name, description, condition, createMautic = true) {
    try {
      const response = await api.post('/segments/create-manual', {
        name,
        description,
        condition,
        create_mautic: createMautic,
      });
      return {
        success: response.data.success !== false,
        message: response.data.message || 'Segment created successfully',
        unomi_result: response.data.unomi_result,
        mautic_result: response.data.mautic_result,
      };
    } catch (error) {
      console.error('Error creating segment manually:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to create segment',
      };
    }
  }

  /**
   * Get Unomi condition field catalog for segment builder
   */
  async getConditionFields() {
    try {
      const response = await api.get('/segments/ui-options/condition-fields');
      const items = response.data?.items;
      if (Array.isArray(items) && items.length > 0) {
        let normalized;
        if (typeof items[0] === 'string') {
          normalized = items
            .filter((p) => p && p !== 'properties.brand')
            .map((prop) => ({
              id: prop,
              label: prop.replace('properties.', ''),
              type: 'string',
            }));
        } else {
          normalized = items.filter((f) => f?.id && f.id !== 'properties.brand');
        }
        const seen = new Set();
        return normalized.filter((f) => {
          if (seen.has(f.id)) return false;
          seen.add(f.id);
          return true;
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.warn('Error fetching condition fields:', error.message);
      }
    }
    return null;
  }

  /**
   * Update segment
   */
  async updateSegment(segmentName, updateData) {
    try {
      console.log(`Updating segment: ${segmentName}`);

      const response = await api.put('/segments/update', {
        segment_name: segmentName,
        ...updateData,
      });

      return {
        success: response.data.success !== false,
        message: response.data.message || 'Segment updated successfully',
        errors: response.data.errors || []
      };
    } catch (error) {
      console.error(`Error updating segment ${segmentName}:`, error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to update segment'
      };
    }
  }

  /**
   * Create segment
   */
  async createSegment(segmentType, segmentConfig) {
    try {
      console.log(`Creating segment of type: ${segmentType}`);

      const response = await api.post('/segments/create', {
        segment_type: segmentType,
        segment_config: segmentConfig
      });

      return {
        success: response.data.success !== false,
        segment: response.data.segment,
        message: response.data.message || 'Segment created successfully'
      };
    } catch (error) {
      console.error(`Error creating segment:`, error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to create segment'
      };
    }
  }

  /**
   * Analyze segment
   */
  async analyzeSegment(query, options = {}) {
    try {
      console.log(`Analyzing segment with query: ${query.substring(0, 100)}...`);
      console.log('Request options:', options);

      // Generate request ID for tracking
      const requestId = `analyze_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const response = await api.post('/segments/analyze', {
        query: query,
        provider: options.provider || "groq",
        model: options.model || "llama-3.1-8b-instant",
        temperature: options.temperature || 0.0
      }, {
        signal: options.signal, // Pass abort signal
        headers: {
          'X-Request-ID': requestId // Send request ID to backend
        }
      });

      console.log('Raw backend response status:', response.status);
      console.log('Raw backend response data:', response.data);

      // Check if response.data exists and has the expected structure
      if (!response.data) {
        console.error('No data in response');
        return {
          success: false,
          message: 'No data received from backend'
        };
      }

      const result = {
        success: response.data.success !== false,
        plain_text_analysis: response.data.plain_text_analysis,
        profile_summary: response.data.profile_summary,
        message: response.data.message,
        segment_name: response.data.segment_name,
        segment_id: response.data.segment_id
      };

      console.log('Mapped response fields:', {
        success: result.success,
        has_plain_text_analysis: !!result.plain_text_analysis,
        plain_text_analysis_length: result.plain_text_analysis?.length,
        has_profile_summary: !!result.profile_summary,
        message: result.message,
        segment_name: result.segment_name,
        segment_id: result.segment_id
      });

      return result;
    } catch (error) {
      console.error(`Error analyzing segment:`, error);
      console.error('Error response data:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to analyze segment'
      };
    }
  }

  /**
   * Get precomputed analysis for a segment
   */
  async getPrecomputedAnalysis(segmentId, abortSignal = null) {
    try {
      console.log(`Fetching precomputed analysis for segment: ${segmentId}`);

      // Ensure proper URL encoding of segment ID
      const encodedSegmentId = encodeURIComponent(segmentId);
      const url = `/segments/${encodedSegmentId}/precomputed-analysis`;

      console.log(`Making API request to: ${url}`);

      // Generate request ID for tracking
      const requestId = `precomputed_${segmentId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const response = await api.get(url, {
        signal: abortSignal, // Add abort signal support
        headers: {
          'X-Request-ID': requestId // Send request ID to backend
        }
      });

      console.log(`API response status: ${response.status}`);
      console.log('API response data:', response.data);

      if (!response.data) {
        console.log(`No precomputed analysis data returned for segment: ${segmentId}`);
        return null;
      }

      const result = {
        success: response.data.success !== false,
        plain_text_analysis: response.data.plain_text_analysis,
        profile_summary: response.data.profile_summary,
        message: response.data.message,
        segment_name: response.data.segment_name,
        segment_id: response.data.segment_id
      };

      console.log(`✅ Successfully retrieved precomputed analysis for segment: ${segmentId}`);
      console.log('Precomputed analysis result:', {
        success: result.success,
        has_plain_text_analysis: !!result.plain_text_analysis,
        plain_text_analysis_length: result.plain_text_analysis?.length,
        has_profile_summary: !!result.profile_summary,
        segment_name: result.segment_name
      });

      return result;
    } catch (error) {
      // Handle aborted requests (cancellation)
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Precomputed analysis request was cancelled');
        throw error; // Re-throw abort errors so they can be handled upstream
      }

      // Improved error handling with proper HTTP status code differentiation
      const status = error.response?.status;
      const statusText = error.response?.statusText;
      const detail = error.response?.data?.detail;

      console.log(`Precomputed analysis request failed for segment ${segmentId}:`, {
        status: status,
        statusText: statusText,
        detail: detail,
        message: error.message,
        url: error.config?.url
      });

      // Handle different error types properly
      if (status === 404) {
        console.log('404 - No precomputed analysis found, will fall back to live analysis');
        return null;
      }

      if (status === 403) {
        console.log('403 - Access denied to precomputed analysis');
        console.log('This might be due to brand/scope mismatch - falling back to live analysis');
        return null;
      }

      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.log('Request timeout - falling back to live analysis');
        return null;
      }

      // For other errors (500, network issues, etc.), log and return null to fallback
      console.warn(`Unexpected error fetching precomputed analysis for ${segmentId}:`, error.message);
      return null;
    }
  }

  /**
   * Query segments
   */
  async querySegments(query, options = {}) {
    try {
      console.log(`Querying segments: ${query.substring(0, 100)}...`);

      const response = await api.post('/segments/query', {
        query: query,
        ...options
      });

      return {
        success: response.data.success !== false,
        segments: response.data.segments || [],
        message: response.data.message
      };
    } catch (error) {
      console.error(`Error querying segments:`, error);
      return {
        success: false,
        segments: [],
        message: error.response?.data?.detail || 'Failed to query segments'
      };
    }
  }

  /**
   * Get segment suggestions
   */
  async getSegmentSuggestions(query, options = {}) {
    try {
      console.log(`Getting segment suggestions for: ${query.substring(0, 100)}...`);

      const response = await api.post('/segments/suggest', {
        query: query,
        ...options
      });

      return {
        success: response.data.success !== false,
        suggestions: response.data.suggestions || [],
        message: response.data.message
      };
    } catch (error) {
      console.error(`Error getting segment suggestions:`, error);
      return {
        success: false,
        suggestions: [],
        message: error.response?.data?.detail || 'Failed to get suggestions'
      };
    }
  }

  /**
   * Find segment by name with enhanced matching
   */
  async findSegmentByNameEnhanced(segmentName) {
    try {
      console.log(`🔍 Enhanced search for segment: "${segmentName}"`);

      // Encode the segment name for URL safety
      const encodedName = encodeURIComponent(segmentName);

      console.log(`🔗 Making request to: /segments/find-by-name/${encodedName}`);

      const response = await api.get(`/segments/find-by-name/${encodedName}`);

      console.log(`📋 Enhanced search response:`, response.data);

      return {
        success: true,
        found: response.data.found,
        segment: response.data.segment,
        segmentId: response.data.segment_id,
        segmentName: response.data.segment_name,
        profileCount: response.data.profile_count,
        matchInfo: response.data.match_info
      };
    } catch (error) {
      console.error(`❌ Error in enhanced segment search:`, error);
      return {
        success: false,
        found: false,
        segment: null,
        error: error.response?.data?.detail || error.message || 'Failed to find segment'
      };
    }
  }

  /**
   * Find segment by name using secure backend endpoint
   */
  async findSegmentByName(segmentName) {
    try {
      console.log(`Finding segment by name: ${segmentName}`);

      const response = await api.get(`/segments/find/${encodeURIComponent(segmentName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error finding segment by name ${segmentName}:`, error);
      throw new Error(error.response?.data?.detail || 'Failed to find segment');
    }
  }

  /**
   * Get segment contact count by name
   */
  async getSegmentContactCount(segmentName) {
    try {
      console.log(`Getting contact count for segment: ${segmentName}`);

      const response = await api.get(`/segments/count/${encodeURIComponent(segmentName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting contact count for ${segmentName}:`, error);
      throw new Error(error.response?.data?.detail || 'Failed to get contact count');
    }
  }

  /**
   * Clear profile count cache
   */
  clearCache() {
    console.log('Clearing segment service cache');
    this.profileCountCache.clear();
  }

  /**
   * Get cache stats for debugging
   */
  getCacheStats() {
    return this.profileCountCache.getStats();
  }

  /**
   * Test method to debug segment analysis
   * This can be called from the browser console to test segment analysis
   */
  async testSegmentAnalysis(segmentName = "Nouveaux leads 237") {
    try {
      console.group('🧪 Testing Segment Analysis');
      console.log('Testing segment analysis for:', segmentName);

      const query = `Fournis une analyse détaillée du segment nommé "${segmentName}".`;
      console.log('Query:', query);

      const startTime = Date.now();
      const result = await this.analyzeSegment(query);
      const endTime = Date.now();

      console.log('⏱️ Analysis took:', (endTime - startTime) + 'ms');
      console.log('📊 Result:', result);

      // Validate result structure
      if (!result) {
        console.error('❌ Result is null or undefined');
        return false;
      }

      if (result.success === false) {
        console.error('❌ Analysis failed:', result.message);
        return false;
      }

      if (!result.plain_text_analysis && !result.profile_summary) {
        console.error('❌ No analysis content returned');
        console.log('Debug - plain_text_analysis:', result.plain_text_analysis);
        console.log('Debug - profile_summary:', result.profile_summary);
        return false;
      }

      console.log('✅ Analysis successful!');
      console.log('📝 Analysis length:', result.plain_text_analysis?.length || 0);
      console.log('📈 Profile summary present:', !!result.profile_summary);

      console.groupEnd();
      return true;

    } catch (error) {
      console.error('❌ Test failed with error:', error);
      console.groupEnd();
      return false;
    }
  }

  /**
   * Get performance statistics from backend and frontend
   */
  async getPerformanceStats() {
    try {
      console.log('Fetching performance statistics...');

      // Get backend performance stats
      const backendResponse = await api.get('/segments/performance/stats');
      const backendStats = backendResponse.data;

      // Get frontend cache stats
      const frontendStats = {
        profile_count_cache: this.profileCountCache.getStats(),
        browser_memory: this._getBrowserMemoryStats()
      };

      return {
        success: true,
        backend: backendStats,
        frontend: frontendStats,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting performance stats:', error);
      return {
        success: false,
        error: error.message,
        frontend: {
          profile_count_cache: this.profileCountCache.getStats(),
          browser_memory: this._getBrowserMemoryStats()
        }
      };
    }
  }

  /**
   * Clear all performance caches on both frontend and backend
   */
  async clearPerformanceCaches() {
    try {
      console.log('🧹 Clearing all performance caches...');

      // Clear frontend caches
      this.profileCountCache.clear();

      // Clear backend caches
      await api.post('/segments/performance/clear-cache');

      // Clear browser cache for this domain (if supported)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (let name of cacheNames) {
          await caches.delete(name);
        }
      }

      console.log('✅ All caches cleared');
      return { success: true, message: 'All caches cleared successfully' };
    } catch (error) {
      console.error('Error clearing caches:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get browser memory statistics if available
   */
  _getBrowserMemoryStats() {
    const stats = {};

    // Check for Performance Memory API (Chrome)
    if (performance && performance.memory) {
      const memory = performance.memory;
      stats.used_js_heap_size_mb = Math.round(memory.usedJSHeapSize / 1024 / 1024);
      stats.total_js_heap_size_mb = Math.round(memory.totalJSHeapSize / 1024 / 1024);
      stats.heap_size_limit_mb = Math.round(memory.jsHeapSizeLimit / 1024 / 1024);
      stats.heap_utilization_percent = Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100);
    }

    // Check navigator memory API (experimental)
    if (navigator && navigator.deviceMemory) {
      stats.device_memory_gb = navigator.deviceMemory;
    }

    return stats;
  }

  /**
   * Synchronize segment deletions between Unomi and Mautic
   * 
   * @param {Array} deletedSegmentNames - Array of segment names that were deleted from Unomi
   * @returns {Promise} Promise resolving to sync results
   */
  async syncSegmentDeletions(deletedSegmentNames) {
    try {
      console.log(`🔄 Syncing ${deletedSegmentNames.length} deleted segments with Mautic:`, deletedSegmentNames);

      if (!deletedSegmentNames || deletedSegmentNames.length === 0) {
        return {
          success: true,
          message: 'No segments to synchronize',
          results: [],
          summary: { total_processed: 0, successful_deletions: 0, not_found: 0, errors: 0 }
        };
      }

      const response = await api.post('/segments/sync-deletions', {
        deleted_segment_names: deletedSegmentNames
      });

      console.log('📊 Sync response:', response.data);

      // Log detailed results for debugging
      if (response.data.results && response.data.results.length > 0) {
        console.log('📋 Detailed sync results:');
        response.data.results.forEach((result, index) => {
          const segmentName = result.search?.input_name || `Segment ${index + 1}`;
          const matches = result.search?.matches?.length || 0;
          const deletions = result.deletion?.deleted_ids?.length || 0;
          const notFound = result.deletion?.not_found ? 'Not found' : '';
          const error = result.deletion?.error || '';

          console.log(`  - ${segmentName}: ${matches} matches, ${deletions} deletions ${notFound} ${error}`.trim());
        });
      }

      // Log summary
      if (response.data.summary) {
        const s = response.data.summary;
        console.log(`✅ Sync summary: ${s.total_processed} processed, ${s.successful_deletions} deleted, ${s.not_found} not found, ${s.errors} errors`);
      }

      return {
        success: response.data.success,
        message: response.data.message,
        results: response.data.results || [],
        summary: response.data.summary || {}
      };

    } catch (error) {
      console.error('❌ Error syncing segment deletions:', error);
      console.error('Error details:', error.response?.data);

      return {
        success: false,
        message: error.response?.data?.detail || error.message || 'Failed to sync segment deletions',
        results: [],
        summary: { total_processed: 0, successful_deletions: 0, not_found: 0, errors: 1 }
      };
    }
  }

  /**
   * Test segment deletion synchronization with the specific case from user
   * This tests the exact scenario that failed: "Leads inactifs depuis plus de 3 mois"
   */
  async testSpecificSegmentSync() {
    const specificSegmentName = "Leads inactifs depuis plus de 3 mois";
    console.group('🧪 Testing Specific Segment Deletion Sync');
    console.log(`Testing sync for the specific segment that failed: "${specificSegmentName}"`);

    try {
      const startTime = Date.now();
      const result = await this.syncSegmentDeletions([specificSegmentName]);
      const endTime = Date.now();

      console.log(`⏱️ Sync took: ${endTime - startTime}ms`);
      console.log('📊 Result:', result);

      // Detailed analysis of the result
      if (result && result.results && result.results.length > 0) {
        const segmentResult = result.results[0];
        console.log('\n📋 Detailed Analysis:');
        console.log(`  Input Name: "${segmentResult.search?.input_name}"`);
        console.log(`  Matches Found: ${segmentResult.search?.matches?.length || 0}`);

        if (segmentResult.search?.matches?.length > 0) {
          segmentResult.search.matches.forEach((match, index) => {
            console.log(`    Match ${index + 1}: ID=${match.id}, Name="${match.name}"`);
          });
        }

        console.log(`  Deletion Attempted: ${segmentResult.deletion?.attempted}`);
        console.log(`  Successful Deletions: ${segmentResult.deletion?.deleted_ids?.length || 0}`);
        console.log(`  Not Found: ${segmentResult.deletion?.not_found}`);
        console.log(`  Error: ${segmentResult.deletion?.error || 'None'}`);

        if (segmentResult.deletion?.deleted_ids?.length > 0) {
          console.log(`  Deleted IDs: ${segmentResult.deletion.deleted_ids.join(', ')}`);
        }
      }

      // Final verdict
      if (result.success) {
        if (result.summary?.successful_deletions > 0) {
          console.log('✅ SUCCESS: Segment was found and deleted from Mautic');
        } else if (result.summary?.not_found > 0) {
          console.log('ℹ️ INFO: Segment was not found in Mautic (might already be deleted)');
        } else {
          console.log('⚠️ WARNING: Sync completed but no deletions or "not found" reported');
        }
      } else {
        console.log('❌ FAILED: Sync operation failed');
      }

      console.groupEnd();
      return result.success;

    } catch (error) {
      console.error('❌ Test failed with error:', error);
      console.groupEnd();
      return false;
    }
  }

  /**
   * Test segment deletion synchronization with sample data
   * This can be called from the browser console to test the sync functionality
   */
  async testSegmentSync(segmentNames = ["Test Segment 1", "Test Segment 2"]) {
    try {
      console.group('🧪 Testing Segment Deletion Sync');
      console.log('Testing segment deletion sync with:', segmentNames);

      const startTime = Date.now();
      const result = await this.syncSegmentDeletions(segmentNames);
      const endTime = Date.now();

      console.log('⏱️ Sync took:', (endTime - startTime) + 'ms');
      console.log('📊 Result:', result);

      // Validate result structure
      if (!result) {
        console.error('❌ Result is null or undefined');
        return false;
      }

      if (result.success === false && result.message) {
        console.warn('⚠️ Sync completed with issues:', result.message);
      } else if (result.success) {
        console.log('✅ Sync successful!');
      }

      console.groupEnd();
      return result.success;

    } catch (error) {
      console.error('❌ Test failed with error:', error);
      console.groupEnd();
      return false;
    }
  }

  /**
   * Diagnostic test for the specific segment synchronization issue
   * This provides detailed debugging information about why sync might fail
   */
  async diagnoseSegmentSyncIssue(segmentName = "Leads inactifs depuis plus de 3 mois") {
    console.group('🔬 Segment Sync Diagnostic');
    console.log(`Diagnosing sync issues for: "${segmentName}"`);

    const diagnostic = {
      segmentName: segmentName,
      timestamp: new Date().toISOString(),
      steps: {},
      issues: [],
      recommendations: []
    };

    try {
      // Step 1: Test basic API connectivity
      console.log('\n🔌 Step 1: Testing API connectivity...');
      try {
        const connectivityTest = await api.get('/segments/performance/stats');
        diagnostic.steps.connectivity = {
          success: true,
          status: connectivityTest.status,
          message: 'API connectivity verified'
        };
        console.log('✅ API connectivity: OK');
      } catch (error) {
        diagnostic.steps.connectivity = {
          success: false,
          error: error.message,
          status: error.response?.status
        };
        diagnostic.issues.push('API connectivity failed');
        console.log('❌ API connectivity: FAILED', error.message);
      }

      // Step 2: Test sync endpoint with diagnostic data
      console.log('\n🧪 Step 2: Testing segment sync endpoint...');
      try {
        const syncTest = await this.syncSegmentDeletions([segmentName]);
        diagnostic.steps.sync = {
          success: syncTest.success,
          message: syncTest.message,
          results: syncTest.results,
          summary: syncTest.summary
        };

        if (syncTest.success) {
          console.log('✅ Sync endpoint: ACCESSIBLE');

          // Analyze results in detail
          if (syncTest.results && syncTest.results.length > 0) {
            const result = syncTest.results[0];

            console.log('\n🔍 Detailed sync analysis:');
            console.log(`   Search input: "${result.search?.input_name}"`);
            console.log(`   Matches found: ${result.search?.matches?.length || 0}`);
            console.log(`   Deletion attempted: ${result.deletion?.attempted}`);
            console.log(`   Successful deletions: ${result.deletion?.deleted_ids?.length || 0}`);
            console.log(`   Not found: ${result.deletion?.not_found}`);
            console.log(`   Error: ${result.deletion?.error || 'None'}`);

            if (result.search?.matches?.length === 0) {
              diagnostic.issues.push('No matching segments found in Mautic');
              diagnostic.recommendations.push('Verify the exact segment name in Mautic');
              diagnostic.recommendations.push('Check if Mautic credentials are correctly configured');
            }

            if (result.deletion?.error) {
              diagnostic.issues.push(`Deletion error: ${result.deletion.error}`);
              diagnostic.recommendations.push('Check Mautic API permissions for segment deletion');
            }
          }
        } else {
          console.log('❌ Sync endpoint: FAILED', syncTest.message);
          diagnostic.issues.push(`Sync endpoint failed: ${syncTest.message}`);
        }

      } catch (error) {
        diagnostic.steps.sync = {
          success: false,
          error: error.message,
          status: error.response?.status
        };
        diagnostic.issues.push(`Sync endpoint error: ${error.message}`);
        console.log('❌ Sync endpoint: ERROR', error.message);
      }

      // Step 3: Provide recommendations
      console.log('\n💡 Recommendations:');
      if (diagnostic.issues.length === 0) {
        console.log('✅ No issues detected - synchronization should work correctly');
        diagnostic.recommendations.push('System appears to be working correctly');
      } else {
        console.log('❌ Issues detected:');
        diagnostic.issues.forEach(issue => console.log(`   - ${issue}`));

        if (diagnostic.recommendations.length === 0) {
          diagnostic.recommendations.push('Check Mautic server connectivity');
          diagnostic.recommendations.push('Verify Mautic credentials are correct');
          diagnostic.recommendations.push('Ensure segment exists in Mautic with exact name');
        }

        console.log('\n🛠️ Suggested fixes:');
        diagnostic.recommendations.forEach(rec => console.log(`   - ${rec}`));
      }

      console.log('\n📋 Full diagnostic report:');
      console.log(JSON.stringify(diagnostic, null, 2));

      console.groupEnd();
      return diagnostic;

    } catch (error) {
      console.error('❌ Diagnostic failed:', error);
      diagnostic.steps.diagnostic = {
        success: false,
        error: error.message
      };
      diagnostic.issues.push(`Diagnostic process failed: ${error.message}`);
      console.groupEnd();
      return diagnostic;
    }
  }

  /**
   * Clear all caches - useful for debugging (deprecated, use clearPerformanceCaches instead)
   */
  clearAllCaches() {
    console.log('⚠️ clearAllCaches is deprecated, use clearPerformanceCaches instead');
    return this.clearPerformanceCaches();
  }
}

// Export singleton instance
const segmentService = new SegmentService();
export default segmentService;

